import { lazy, Suspense } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { ConfirmDeleteProvider } from "@/components/ConfirmDeleteProvider";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/use-auth";
import { BranchProvider } from "@/hooks/use-branch";
import { ThemeProvider } from "@/hooks/use-theme";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageLoader } from "@/components/PageLoader";

import Login from "./pages/Login";
import Home from "./pages/Home";

const Dashboard = lazy(() => import("./pages/dashboard"));
const POS = lazy(() => import("./pages/pos"));
const OrdersList = lazy(() => import("./pages/OrdersList"));
const ReservationOrders = lazy(() => import("./pages/ReservationOrders"));
const Menu = lazy(() => import("./pages/menu"));
const Kitchen = lazy(() => import("./pages/Kitchen"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Crockery = lazy(() => import("./pages/Crockery"));
const Recipes = lazy(() => import("./pages/Recipes"));
const Staff = lazy(() => import("./pages/Staff"));
const StaffDetail = lazy(() => import("./pages/StaffDetail"));
const Vendors = lazy(() => import("./pages/Vendors"));
const VendorDetail = lazy(() => import("./pages/VendorDetail"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Expenses = lazy(() => import("./pages/Expenses"));
const Reports = lazy(() => import("./pages/Reports"));
const ActivityLogs = lazy(() => import("./pages/ActivityLogs"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("@/pages/not-found"));

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/" component={Home} />
        <Route path="/dashboard">
          <ProtectedRoute path="/dashboard" component={Dashboard} />
        </Route>
        <Route path="/pos">
          <ProtectedRoute path="/pos" component={POS} />
        </Route>
        <Route path="/order-list">
          <ProtectedRoute path="/order-list" component={OrdersList} />
        </Route>
        <Route path="/reservation-orders">
          <ProtectedRoute path="/reservation-orders" component={ReservationOrders} />
        </Route>
        <Route path="/menu">
          <ProtectedRoute path="/menu" component={Menu} />
        </Route>
        <Route path="/kitchen">
          <ProtectedRoute path="/kitchen" component={Kitchen} />
        </Route>
        <Route path="/inventory">
          <ProtectedRoute path="/inventory" component={Inventory} />
        </Route>
        <Route path="/crockery">
          <ProtectedRoute path="/crockery" component={Crockery} />
        </Route>
        <Route path="/recipes">
          <ProtectedRoute path="/recipes" component={Recipes} />
        </Route>
        <Route path="/staff">
          <ProtectedRoute path="/staff" component={Staff} />
        </Route>
        <Route path="/staff/:id">
          <ProtectedRoute path="/staff" component={StaffDetail} />
        </Route>
        <Route path="/vendors">
          <ProtectedRoute path="/vendors" component={Vendors} />
        </Route>
        <Route path="/vendors/:id">
          <ProtectedRoute path="/vendors" component={VendorDetail} />
        </Route>
        <Route path="/transactions">
          <ProtectedRoute path="/transactions" component={Transactions} />
        </Route>
        <Route path="/expenses">
          <ProtectedRoute path="/expenses" component={Expenses} />
        </Route>
        <Route path="/reports">
          <ProtectedRoute path="/reports" component={Reports} />
        </Route>
        <Route path="/activity-logs">
          <ProtectedRoute path="/activity-logs" component={ActivityLogs} />
        </Route>
        <Route path="/settings">
          <ProtectedRoute path="/settings" component={Settings} />
        </Route>
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BranchProvider>
            <TooltipProvider>
              <ConfirmDeleteProvider>
                <Router />
              </ConfirmDeleteProvider>
              <Toaster />
            </TooltipProvider>
          </BranchProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
